import React, { useState, useEffect } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { FaBars, FaTrash, FaTimes } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../Login/AuthContext";
import { useCarrito } from "./ContextoCarrito";

const ListaProductos = ({ modo = "cliente" }) => {
  const [productos, setProductos] = useState([]);
  const [filteredProductos, setFilteredProductos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [productoSeleccionado, setProductoSeleccionado] = useState(null);
  const [cantidades, setCantidades] = useState({});

  const [filtros, setFiltros] = useState({
    nombre: "",
    descripcion: "",
    precioMin: "",
    precioMax: "",
    stock: "",
    categoria: "",
  });

  const [ordenamiento, setOrdenamiento] = useState("relevancia");
  const [itemsPorPagina, setItemsPorPagina] = useState(12);
  const [vistaLayout, setVistaLayout] = useState("grid"); // grid o list
  const [filtrosAbiertos, setFiltrosAbiertos] = useState(false);

  const API_URL = process.env.REACT_APP_API_URL;
  const BASE_URL = process.env.REACT_APP_BACKEND_URL;
  const { token, usuario } = useAuth();
  const { agregarAlCarrito } = useCarrito();
  const navigate = useNavigate();

  useEffect(() => {
  const controller = new AbortController();
  let activo = true;

  const fetchProductos = async () => {
    try {
      const response = await axios.get(`${API_URL}/productos`, {
        signal: controller.signal,
      });

      if (Array.isArray(response.data)) {
        if (!activo) return;
        setProductos(response.data);
        setFilteredProductos(response.data);

        const cantidadesIniciales = {};
        response.data.forEach((p) => {
          cantidadesIniciales[p.id] = 1;
        });

        setCantidades(cantidadesIniciales);
      } else {
        if (!activo) return;
        setError("Error: Los datos no son un arreglo.");
      }
    } catch (err) {
      if (axios.isCancel(err)) return;
      setError("Error al cargar los productos. Intente nuevamente.");
    } finally {
      if (activo) setLoading(false);
    }
  };

  fetchProductos();

  return () => {
    activo = false;
    controller.abort();
  };
}, [API_URL]);

  useEffect(() => {
    filtrarProductos(filtros);
  }, [ordenamiento]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    const nuevosFiltros = { ...filtros, [name]: value };
    setFiltros(nuevosFiltros);
    filtrarProductos(nuevosFiltros);
  };

  const filtrarProductos = (filtrosActuales) => {
    let productosFiltrados = productos.filter((producto) => {
      const categoriaNombre = producto.categoria?.nombre || "";
      const precioMin = Number(filtrosActuales.precioMin);
      const precioMax = Number(filtrosActuales.precioMax);
      const productoPrecio = Number(producto.precio);
      const cumplePrecioMin =
        !filtrosActuales.precioMin || productoPrecio >= precioMin;
      const cumplePrecioMax =
        !filtrosActuales.precioMax || productoPrecio <= precioMax;

      return (
        producto.nombre?.toLowerCase().includes(filtrosActuales.nombre.toLowerCase()) &&
        (producto.descripcion || "").toLowerCase().includes(filtrosActuales.descripcion.toLowerCase()) &&
        cumplePrecioMin &&
        cumplePrecioMax &&
        producto.stock?.toString().includes(filtrosActuales.stock) &&
        categoriaNombre.toLowerCase().includes(filtrosActuales.categoria.toLowerCase())
      );
    });

    // Aplicar ordenamiento
    if (ordenamiento === "menor-precio") {
      productosFiltrados.sort((a, b) => a.precio - b.precio);
    } else if (ordenamiento === "mayor-precio") {
      productosFiltrados.sort((a, b) => b.precio - a.precio);
    }

    setFilteredProductos(productosFiltrados);
  };

  const handleCantidadChange = (productoId, valor, stock) => {
    let nuevaCantidad = Number(valor);
    if (isNaN(nuevaCantidad) || nuevaCantidad < 1) nuevaCantidad = 1;
    if (nuevaCantidad > stock) nuevaCantidad = stock;
    setCantidades((prev) => ({ ...prev, [productoId]: nuevaCantidad }));
  };

  // ─── Verificar login antes de agregar al carrito ─────────────
  const handleAgregarAlCarrito = async (producto, cantidad = 1) => {
  if (!usuario) {
    Swal.fire({
      icon: "info",
      title: "Iniciá sesión",
      text: "Necesitás una cuenta para agregar productos al carrito.",
      confirmButtonText: "Iniciar sesión",
      showCancelButton: true,
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#dc2626",
    }).then((result) => {
      if (result.isConfirmed) navigate("/login");
    });
    return;
  }

  const ok = await agregarAlCarrito(producto, cantidad);

  if (ok) {
    Swal.fire({
      icon: "success",
      title: "Añadido al carrito",
      text: `${producto.nombre} agregado correctamente`,
      timer: 1500,
      showConfirmButton: false,
    });
  }
};

  const eliminarProducto = async (id) => {
    const result = await Swal.fire({
      title: "¿Estás seguro?",
      text: "No podrás revertir esta acción.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#e11d48",
      cancelButtonColor: "#64748b",
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
    });

    if (result.isConfirmed) {
      try {
        await axios.delete(`${API_URL}/productos/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setProductos(productos.filter((p) => p.id !== id));
        setFilteredProductos(filteredProductos.filter((p) => p.id !== id));
        Swal.fire("Eliminado", "El producto ha sido eliminado.", "success");
      } catch (error) {
        Swal.fire("Error", "No se pudo eliminar el producto.", "error");
      }
    }
  };

  const abrirModal = (producto) => setProductoSeleccionado(producto);
  const cerrarModal = () => setProductoSeleccionado(null);

  if (loading) return (
    <div className="flex justify-center items-center min-h-screen bg-white">
      <p className="text-gray-600 text-lg animate-pulse">Cargando productos...</p>
    </div>
  );

  if (error) return (
    <div className="flex justify-center items-center min-h-screen bg-white">
      <p className="text-gray-600 text-lg">{error}</p>
    </div>
  );

  if (productos.length === 0) return (
    <div className="flex justify-center items-center min-h-screen bg-white">
      <p className="text-gray-600 text-lg">No hay productos registrados.</p>
    </div>
  );

  // Obtener todas las categorías únicas
  const categorias = [
    ...new Set(
      productos
        .filter((p) => p.categoria?.nombre)
        .map((p) => p.categoria.nombre)
    ),
  ];

  return (
    <section className="min-h-screen bg-white">
      {modo === "admin" ? (
        // ─── VISTA ADMIN ──────────────────────────────────────────────
        <div className="p-4 md:p-8">
          <div className="w-full max-w-7xl mx-auto">
            <h2 className="text-2xl md:text-4xl font-extrabold text-center mb-6 md:mb-8 text-black">
              Listado de Productos
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
              {["nombre", "descripcion", "precio", "stock", "categoria"].map((campo) => (
                <input
                  key={campo}
                  type="text"
                  name={campo}
                  placeholder={`Filtrar por ${campo}`}
                  value={filtros[campo]}
                  onChange={handleFilterChange}
                  className="border border-gray-300 rounded-md p-3 bg-white focus:outline-none focus:ring-2 focus:ring-gray-400"
                />
              ))}
            </div>

            <div className="overflow-x-auto bg-white rounded-xl shadow-md p-4 border border-gray-300">
              <table className="table-auto w-full min-w-[900px] border-collapse border border-gray-300">
                <thead>
                  <tr className="bg-gray-200 text-black">
                    <th className="border border-gray-300 px-4 py-2">Imagen</th>
                    <th className="border border-gray-300 px-4 py-2">Nombre</th>
                    <th className="border border-gray-300 px-4 py-2">Descripción</th>
                    <th className="border border-gray-300 px-4 py-2">Precio</th>
                    <th className="border border-gray-300 px-4 py-2">Stock</th>
                    <th className="border border-gray-300 px-4 py-2">Categoría</th>
                    <th className="border border-gray-300 px-4 py-2">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredProductos.map((producto, index) => (
                    <tr key={producto.id} className={`${index % 2 === 0 ? "bg-gray-50" : "bg-white"} hover:bg-gray-100`}>
                      <td className="border border-gray-300 px-4 py-2 text-center">
                        {producto.imagen_url ? (
                          <img src={`${BASE_URL}${producto.imagen_url}`} alt={producto.nombre} className="w-16 h-16 object-cover rounded mx-auto" />
                        ) : (
                          <span className="text-gray-400">Sin imagen</span>
                        )}
                      </td>
                      <td className="border border-gray-300 px-4 py-2">{producto.nombre}</td>
                      <td className="border border-gray-300 px-4 py-2">
                        {producto.descripcion ? <DescripcionExpandable descripcion={producto.descripcion} /> : ""}
                      </td>
                      <td className="border border-gray-300 px-4 py-2 text-black font-semibold">$ {producto.precio}</td>
                      <td className="border border-gray-300 px-4 py-2">{producto.stock}</td>
                      <td className="border border-gray-300 px-4 py-2">{producto.categoria?.nombre || ""}</td>
                      <td className="border border-gray-300 px-4 py-2 text-center">
                        <button onClick={() => eliminarProducto(producto.id)} className="text-black hover:text-gray-700" title="Eliminar">
                          <FaTrash />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ) : (
        // ─── VISTA CLIENTE ────────────────────────────────────────────
        <div className="flex flex-col lg:flex-row gap-6 p-4 md:p-6 max-w-full">
          {/* ─── SIDEBAR IZQUIERDO ───────────────────────────────────── */}
          <button
            type="button"
            onClick={() => setFiltrosAbiertos((abiertos) => !abiertos)}
            className="lg:hidden inline-flex items-center justify-center gap-2 w-full border border-gray-300 rounded px-4 py-3 text-sm font-semibold text-gray-900 bg-white hover:bg-gray-50 transition"
            aria-expanded={filtrosAbiertos}
          >
            {filtrosAbiertos ? <FaTimes /> : <FaBars />}
            Filtros
          </button>

          <aside className={`${filtrosAbiertos ? "block" : "hidden"} lg:block w-full lg:w-64 flex-shrink-0 bg-white p-4 md:p-6 border border-gray-200 lg:border-t-0 lg:border-l-0 lg:border-b-0 lg:border-r h-fit lg:sticky lg:top-24`}>
            {/* Categorías */}
            <div className="mb-8">
              <h3 className="text-lg font-bold text-gray-900 mb-4">CATEGORIAS</h3>
              <ul className="space-y-2">
                <li>
                  <button
                    onClick={() => {
                      setFiltros({ ...filtros, categoria: "" });
                      filtrarProductos({ ...filtros, categoria: "" });
                      setFiltrosAbiertos(false);
                    }}
                    className={`w-full text-left text-sm py-2 px-3 rounded transition ${
                      filtros.categoria === ""
                        ? "bg-gray-100 text-gray-900 font-semibold"
                        : "text-gray-600 hover:text-gray-900"
                    }`}
                  >
                    Todos
                  </button>
                </li>
                {categorias.map((categoria) => (
                  <li key={categoria}>
                    <button
                      onClick={() => {
                        const nuevosFiltros = { ...filtros, categoria };
                        setFiltros(nuevosFiltros);
                        filtrarProductos(nuevosFiltros);
                        setFiltrosAbiertos(false);
                      }}
                      className={`w-full text-left text-sm py-2 px-3 rounded transition ${
                        filtros.categoria === categoria
                          ? "bg-gray-100 text-gray-900 font-semibold"
                          : "text-gray-600 hover:text-gray-900"
                      }`}
                    >
                      {categoria}
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            {/* Filtro de Precio */}
            <div>
              <h3 className="text-sm font-bold text-gray-900 mb-4">PRECIO</h3>
              <div className="space-y-3">
                <input
                  type="number"
                  name="precioMin"
                  min="0"
                  placeholder="Desde"
                  value={filtros.precioMin}
                  onChange={handleFilterChange}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-400"
                />
                <input
                  type="number"
                  name="precioMax"
                  min="0"
                  placeholder="Hasta"
                  value={filtros.precioMax}
                  onChange={handleFilterChange}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-400"
                />
                <button
                  type="button"
                  onClick={() => {
                    const nuevosFiltros = {
                      ...filtros,
                      precioMin: "",
                      precioMax: "",
                    };
                    setFiltros(nuevosFiltros);
                    filtrarProductos(nuevosFiltros);
                  }}
                  className="text-xs text-gray-600 hover:text-black hover:underline"
                >
                  Limpiar precio
                </button>
              </div>
            </div>
          </aside>

          {/* ─── CONTENIDO PRINCIPAL ──────────────────────────────────── */}
          <main className="flex-1 min-w-0">
            {/* Header con opciones */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8 pb-6 border-b border-gray-200">
              <h1 className="text-xl md:text-2xl font-bold text-gray-900">
                {filtros.categoria ? filtros.categoria : "Todos los productos"}
              </h1>
              <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:flex-wrap sm:items-center sm:gap-4">
                <div className="flex w-full items-center gap-2 sm:w-auto">
                  <label className="text-sm text-gray-600">Order by:</label>
                  <select 
                    value={ordenamiento}
                    onChange={(e) => setOrdenamiento(e.target.value)}
                    className="min-w-0 flex-1 border border-gray-300 rounded px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-gray-400 sm:flex-none"
                  >
                    <option value="relevancia">Relevancia</option>
                    <option value="menor-precio">Menor precio</option>
                    <option value="mayor-precio">Mayor precio</option>
                  </select>
                </div>
                <div className="flex w-full items-center gap-2 sm:w-auto">
                  <label className="text-sm text-gray-600">Mostrar:</label>
                  <select 
                    value={itemsPorPagina}
                    onChange={(e) => setItemsPorPagina(Number(e.target.value))}
                    className="min-w-0 flex-1 border border-gray-300 rounded px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-gray-400 sm:flex-none"
                  >
                    <option value="12">12</option>
                    <option value="24">24</option>
                    <option value="36">36</option>
                  </select>
                </div>
                <div className="flex w-full justify-center gap-1 border border-gray-300 rounded p-1 sm:w-auto">
                  <button 
                    onClick={() => setVistaLayout("grid")}
                    className={`p-1 rounded transition ${vistaLayout === "grid" ? "bg-gray-100" : "hover:bg-gray-50"}`}
                  >
                    ⊞⊞
                  </button>
                  <button 
                    onClick={() => setVistaLayout("list")}
                    className={`p-1 rounded transition ${vistaLayout === "list" ? "bg-gray-100" : "hover:bg-gray-50"}`}
                  >
                    ≡
                  </button>
                </div>
              </div>
            </div>

            {/* Grid/Lista de Productos */}
            <div className={vistaLayout === "grid" ? "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-12" : "space-y-4"}>
              {filteredProductos.slice(0, itemsPorPagina).map((producto) => {
                const imageUrl = producto.imagen_url
                  ? `${BASE_URL}${producto.imagen_url}`
                  : "https://via.placeholder.com/400x500?text=Sin+imagen";

                if (vistaLayout === "list") {
                  // Vista de lista (horizontal)
                  return (
                    <div
                      key={producto.id}
                      className="flex flex-col sm:flex-row gap-4 bg-white rounded-lg overflow-hidden hover:shadow-lg transition duration-300 cursor-pointer border border-gray-200 p-4"
                      onClick={() => navigate(`/producto/${producto.id}`)}
                    >
                      <div className="w-full sm:w-40 h-56 sm:h-40 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                        <img
                          src={imageUrl}
                          alt={producto.nombre}
                          className="w-full h-full object-cover hover:scale-105 transition duration-300"
                        />
                      </div>

                      <div className="flex flex-col justify-between flex-1">
                        <div>
                          <p className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-1">
                            {producto.categoria?.nombre || "Sin categoría"}
                          </p>
                          <h3 className="text-lg font-semibold text-black mb-2">
                            {producto.nombre}
                          </h3>
                          <p className="text-sm text-gray-600">
                            {producto.descripcion ? producto.descripcion.slice(0, 100) + "..." : "Sin descripción"}
                          </p>
                        </div>

                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mt-4 sm:mt-0">
                          <p className="text-2xl font-bold text-black">
                            ${Number(producto.precio).toLocaleString('es-AR')}
                          </p>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAgregarAlCarrito(producto, 1);
                            }}
                            disabled={producto.stock === 0}
                            className="w-full sm:w-auto px-6 py-2 bg-black text-white rounded font-semibold text-sm hover:bg-gray-900 transition disabled:opacity-50 disabled:cursor-not-allowed uppercase tracking-wide"
                          >
                            {producto.stock === 0 ? "SIN STOCK" : "Añadir"}
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                }

                // Vista de grid (por defecto)
                return (
                  <div 
                    key={producto.id} 
                    className="flex flex-col bg-white rounded-lg overflow-hidden hover:shadow-2xl transition duration-300 cursor-pointer transform hover:-translate-y-1"
                    onClick={() => navigate(`/producto/${producto.id}`)}
                  >
                    {/* Imagen con sombra */}
                    <div className="w-full h-64 md:h-72 bg-gray-100 overflow-hidden rounded-lg mb-4 shadow-lg">
                      <img
                        src={imageUrl}
                        alt={producto.nombre}
                        className="w-full h-full object-cover hover:scale-105 transition duration-300"
                      />
                    </div>

                    {/* Info - Se expande para llenar el espacio */}
                    <div className="flex flex-col justify-between flex-1 px-3 pb-3">
                      <div>
                        <p className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-2">
                          {producto.categoria?.nombre || "Sin categoría"}
                        </p>
                        <h3 className="text-sm font-semibold text-black mb-2 line-clamp-2">
                          {producto.nombre}
                        </h3>
                      </div>
                      
                      <div className="mt-auto">
                        <p className="text-xl font-bold text-black mb-3">
                          ${Number(producto.precio).toLocaleString('es-AR')}
                        </p>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleAgregarAlCarrito(producto, 1);
                          }}
                          disabled={producto.stock === 0}
                          className="w-full bg-black text-white py-2 rounded font-semibold text-xs hover:bg-gray-900 transition disabled:opacity-50 disabled:cursor-not-allowed uppercase tracking-wide"
                        >
                          {producto.stock === 0 ? "SIN STOCK" : "Añadir al carrito"}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </main>
        </div>
      )}

      {productoSeleccionado && (
        <ModalProducto
          producto={productoSeleccionado}
          baseUrl={BASE_URL}
          onClose={cerrarModal}
          onAgregar={handleAgregarAlCarrito}
        />
      )}
    </section>
  );
};

const DescripcionExpandable = ({ descripcion }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  return (
    <div>
      <p className="text-gray-700">
        {isExpanded ? descripcion : descripcion?.slice(0, 50) + (descripcion?.length > 50 ? "..." : "")}
      </p>
      {descripcion && descripcion.length > 50 && (
        <button onClick={() => setIsExpanded(!isExpanded)} className="text-black hover:text-gray-700 hover:underline text-sm">
          {isExpanded ? "Ver menos" : "Ver más"}
        </button>
      )}
    </div>
  );
};

const ModalProducto = ({ producto, baseUrl, onClose, onAgregar }) => {
  const imageUrl = producto.imagen_url
    ? `${baseUrl}${producto.imagen_url}`
    : "https://via.placeholder.com/500x700?text=Sin+imagen";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4 py-4 overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[95vh] overflow-y-auto relative border border-gray-300">
        <button onClick={onClose} className="absolute top-4 right-4 text-gray-600 hover:text-black text-xl z-10">
          <FaTimes />
        </button>
        <div className="grid grid-cols-1 md:grid-cols-2">
          <div className="bg-gray-100 h-72 sm:h-[500px] md:h-[650px]">
            <img src={imageUrl} alt={producto.nombre} className="w-full h-full object-cover" />
          </div>
          <div className="p-5 md:p-8 flex flex-col justify-center">
            <p className="text-sm uppercase tracking-widest text-gray-600 mb-2">
              {producto.categoria?.nombre || "Sin categoría"}
            </p>
            <h2 className="text-2xl font-bold text-gray-900 mb-4 break-words sm:text-3xl">{producto.nombre}</h2>
            <p className="text-2xl font-extrabold text-black mb-6">$ {producto.precio}</p>
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Descripción</h3>
              <p className="text-gray-600 leading-relaxed">
                {producto.descripcion || "Sin descripción disponible."}
              </p>
            </div>
            <p className="text-sm text-gray-500 mb-6">
              Stock disponible: <span className="font-semibold">{producto.stock}</span>
            </p>
            <button
              onClick={() => onAgregar(producto, 1)}
              disabled={producto.stock === 0}
              className="bg-black text-white px-6 py-3 rounded-lg hover:bg-gray-900 transition w-full md:w-auto disabled:opacity-50"
            >
              {producto.stock === 0 ? "Sin stock" : "🛒 Agregar al carrito"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ListaProductos;
